import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Download, Loader, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

/**
 * InvoiceDetailModal — tenant portal invoice detail + PDF download.
 *
 * Props:
 *   invoiceId : string  (the invoice _id to load)
 *   onClose   : () => void
 *   onPay     : (invoice) => void   (optional — fires when Pay Now clicked)
 *
 * Data flow:
 *   1. Mount -> GET /api/tenant/me/invoices/{id} -> render summary.
 *   2. Download PDF -> GET /api/tenant/me/invoices/{id}/pdf as blob ->
 *      trigger browser download via a transient anchor.
 *
 * Backend filters by customer_id from token so this modal can only ever
 * render the caller's own invoices — passing someone else's ID 404s.
 */

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const s = String(iso);
  // Bare calendar dates (due_date) render as-is — never shifted. Stored
  // naive-UTC TIMESTAMPS render as the instant's local date rather than
  // slicing the UTC prefix, which read a day ahead every evening (the
  // Claire Allen move-out class, 2026-08-01). The tenant viewing their
  // own facility's portal is in (or near) the facility's zone.
  const isDateOnly = !(s.includes('T') || /\d{1,2}:\d{2}/.test(s));
  try {
    const dt = isDateOnly ? new Date(`${s}T00:00:00Z`) : new Date(`${s.replace(/[zZ]$/, '')}Z`);
    if (Number.isNaN(dt.getTime())) return s.slice(0, 10);
    return dt.toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
      ...(isDateOnly ? { timeZone: 'UTC' } : {}),
    });
  } catch {
    return s.slice(0, 10);
  }
};

const statusPill = (status) => {
  const s = (status || 'pending').toLowerCase();
  const cls = s === 'paid'
    ? 'bg-emerald-50 text-emerald-600'
    : s === 'overdue'
      ? 'bg-red-50 text-red-500'
      : s === 'void'
        ? 'bg-slate-100 text-slate-500'
        : 'bg-amber-50 text-amber-600';
  return (
    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${cls}`}>
      {s.toUpperCase()}
    </span>
  );
};

const InvoiceDetailModal = ({ invoiceId, onClose, onPay }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('tenantAccessToken');
    if (!token) return null;
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    if (!invoiceId) return;
    const headers = getHeaders();
    if (!headers) { setError('Session expired.'); setLoading(false); return; }
    setLoading(true);
    axios
      .get(`${API_URL}/api/tenant/me/invoices/${invoiceId}`, { headers })
      .then((res) => setDetail(res.data))
      .catch((err) => {
        setError(err.response?.data?.detail || 'Could not load invoice.');
      })
      .finally(() => setLoading(false));
  }, [invoiceId, getHeaders]);

  const handleDownload = async () => {
    const headers = getHeaders();
    if (!headers || !detail) return;
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/tenant/me/invoices/${invoiceId}/pdf`,
        { headers, responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const invNo = detail.invoice?.invoice_number || invoiceId;
      a.download = `invoice-${invNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Slight delay before revoke so the browser has the blob handle.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not generate PDF.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-slate-500" />
            <h2 className="text-[14px] font-semibold text-slate-900">
              Invoice {detail?.invoice?.invoice_number || ''}
            </h2>
            {detail && statusPill(detail.invoice?.status)}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          )}

          {error && !loading && (
            <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {detail && !loading && (
            <>
              {/* Top meta: invoice date / due date / period */}
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Invoice Date</p>
                  <p className="text-[13px] text-slate-700 mt-0.5">
                    {fmtDate(detail.invoice?.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Due Date</p>
                  <p className="text-[13px] text-slate-700 mt-0.5">
                    {fmtDate(detail.invoice?.due_date)}
                  </p>
                </div>
                {detail.invoice?.period_start && detail.invoice?.period_end && (
                  <div className="col-span-2">
                    <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Service Period</p>
                    <p className="text-[13px] text-slate-700 mt-0.5">
                      {fmtDate(detail.invoice.period_start)} to {fmtDate(detail.invoice.period_end)}
                    </p>
                  </div>
                )}
              </div>

              {/* Bill to / From */}
              <div className="grid grid-cols-2 gap-4 mb-5 pb-5 border-b border-slate-100">
                <div>
                  <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1">Bill To</p>
                  <p className="text-[13px] font-semibold text-slate-900">{detail.tenant?.full_name}</p>
                  {detail.tenant?.customer_number && (
                    <p className="text-[12px] text-slate-500">Customer #: {detail.tenant.customer_number}</p>
                  )}
                  {detail.tenant?.email && (
                    <p className="text-[12px] text-slate-500">{detail.tenant.email}</p>
                  )}
                  {detail.tenant?.phone && (
                    <p className="text-[12px] text-slate-500">{detail.tenant.phone}</p>
                  )}
                  {detail.tenant?.address && (
                    <p className="text-[12px] text-slate-500 mt-1">{detail.tenant.address}</p>
                  )}
                  {(detail.tenant?.city || detail.tenant?.state || detail.tenant?.zip_code) && (
                    <p className="text-[12px] text-slate-500">
                      {[detail.tenant.city, detail.tenant.state, detail.tenant.zip_code].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1">From</p>
                  <p className="text-[13px] font-semibold text-slate-900">{detail.facility?.name}</p>
                  {detail.facility?.address && (
                    <p className="text-[12px] text-slate-500 mt-1">{detail.facility.address}</p>
                  )}
                  {(detail.facility?.city || detail.facility?.state || detail.facility?.zip_code) && (
                    <p className="text-[12px] text-slate-500">
                      {[detail.facility.city, detail.facility.state, detail.facility.zip_code].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {detail.facility?.phone && (
                    <p className="text-[12px] text-slate-500">{detail.facility.phone}</p>
                  )}
                </div>
              </div>

              {/* Line items */}
              <div className="mb-5">
                <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-2">Line Items</p>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left text-[11px] font-medium text-slate-400 uppercase tracking-wider py-1.5">Description</th>
                        <th className="text-right text-[11px] font-medium text-slate-400 uppercase tracking-wider py-1.5">Amount</th>
                        <th className="text-right text-[11px] font-medium text-slate-400 uppercase tracking-wider py-1.5">Tax</th>
                        <th className="text-right text-[11px] font-medium text-slate-400 uppercase tracking-wider py-1.5">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {(detail.invoice?.line_items || []).map((li, idx) => {
                        const amt = Number(li.amount || 0);
                        const tax = Number(li.tax_amount || 0);
                        return (
                          <tr key={idx}>
                            <td className="py-2 text-[13px] text-slate-700">{li.description}</td>
                            <td className="py-2 text-[13px] text-slate-600 text-right">{fmtMoney(amt)}</td>
                            <td className="py-2 text-[13px] text-slate-600 text-right">{fmtMoney(tax)}</td>
                            <td className="py-2 text-[13px] font-medium text-slate-700 text-right">{fmtMoney(amt + tax)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="ml-auto max-w-xs space-y-1.5 mb-5">
                <div className="flex items-center justify-between text-[13px]">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="text-slate-700">{fmtMoney(detail.invoice?.amount)}</span>
                </div>
                <div className="flex items-center justify-between text-[13px]">
                  <span className="text-slate-500">Tax</span>
                  <span className="text-slate-700">{fmtMoney(detail.invoice?.tax_amount)}</span>
                </div>
                {Number(detail.invoice?.late_fee_amount || 0) > 0 && (
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-500">Late fee</span>
                    <span className="text-slate-700">{fmtMoney(detail.invoice.late_fee_amount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-[14px] font-semibold pt-1.5 border-t border-slate-200">
                  <span className="text-slate-900">Total</span>
                  <span className="text-slate-900">{fmtMoney(detail.invoice?.total)}</span>
                </div>
              </div>

              {/* Payment status */}
              {detail.invoice?.status === 'paid' ? (
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2.5 mb-4">
                  <p className="text-[11px] font-medium text-emerald-600 uppercase tracking-wider">Paid</p>
                  <p className="text-[13px] text-emerald-700 mt-0.5">
                    {fmtMoney(detail.invoice?.total)} on {fmtDate(detail.payment?.paid_at || detail.invoice?.paid_at)}
                    {detail.payment?.method && ` · ${detail.payment.method}`}
                    {detail.payment?.last4 && ` ending in ${detail.payment.last4}`}
                  </p>
                </div>
              ) : detail.invoice?.status === 'void' ? (
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 mb-4">
                  <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Voided</p>
                  <p className="text-[13px] text-slate-600 mt-0.5">This invoice has been voided and is no longer due.</p>
                </div>
              ) : (
                <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2.5 mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-medium text-amber-600 uppercase tracking-wider">Amount Due</p>
                    <p className="text-[14px] font-semibold text-amber-700 mt-0.5">
                      {fmtMoney(detail.invoice?.total)} by {fmtDate(detail.invoice?.due_date)}
                    </p>
                  </div>
                  {onPay && (
                    <button
                      onClick={() => onPay(detail.invoice)}
                      className="rounded-lg bg-blue-600 text-white text-[12px] font-medium px-4 py-1.5 hover:bg-blue-700 transition-colors whitespace-nowrap"
                    >
                      Pay Now
                    </button>
                  )}
                </div>
              )}

              {/* Notes */}
              {detail.invoice?.notes && (
                <div className="mb-2">
                  <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1">Notes</p>
                  <p className="text-[13px] text-slate-600 whitespace-pre-line">{detail.invoice.notes}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/40">
          <button
            onClick={onClose}
            className="text-[13px] font-medium text-slate-500 hover:text-slate-700"
          >
            Close
          </button>
          <button
            onClick={handleDownload}
            disabled={!detail || downloading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-medium px-3.5 py-1.5 hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {downloading ? (
              <Loader className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailModal;
